import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "login-screen" }
const _hoisted_2 = { class: "login-card" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "mt-2" }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "mt-2" }
const _hoisted_7 = { class: "reset-actions" }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { class: "mt-2" }

import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppOTPField from "@/shared/components/ui/AppOTPField.vue";
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import { resetPasswordOTP, resetPassword, passwordVerifyOTP } from "@/modules/auth/services/auth.api";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { useAppStore } from '@/shared/stores/app';
import fallbackLogo from '@/assets/s-logo.png';
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'ResetPasswordPage',
  setup(__props) {

const step = ref<1 | 2 | 3>(1);
const email = ref("");
const otp = ref("");
const otpError = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const password = ref("");
const confirmPassword = ref("");
const isSendingOtp = ref(false);
const isVerifyingOtp = ref(false);
const isResettingPassword = ref(false);
const router = useRouter();
const emailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const snackbar = useSnackbarStore();
const appStore = useAppStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim(),
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => {
  if (step.value === 1) return "Enter your email to receive a verification code.";
  if (step.value === 2) return `Enter the code sent to ${email.value || "your email"}.`;
  return "Set a new password for your account.";
});
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

function goToStep(nextStep: 1 | 2 | 3) {
  // Clear step-specific errors
  emailError.value = "";
  otpError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (nextStep === 1) {
    otp.value = "";
    password.value = "";
    confirmPassword.value = "";
  }

  if (nextStep === 2) {
    password.value = "";
    confirmPassword.value = "";
  }

  step.value = nextStep;
}

function validateInputs() {
  emailError.value = "";

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  return !emailError.value;
}
function validateOtp(length = 6) {
  otpError.value = "";
  const raw = otp.value?.toString().trim() || "";

  if (!raw) {
    otpError.value = "OTP is required";
    return false;
  }

  if (!/^\d+$/.test(raw)) {
    otpError.value = "OTP must be numbers only";
    return false;
  }

  if (raw.length !== length) {
    otpError.value = "Enter a valid OTP";
    return false;
  }

  return true;
}

async function handleVerifyOtp(_value?: unknown) {
  if (isVerifyingOtp.value) return;
  isVerifyingOtp.value = true;
  try {
    // `@finish` emits the OTP string, but `@click="handleVerifyOtp"` passes a MouseEvent.
    if (typeof _value === "string") otp.value = _value;

    if (!validateInputs()) {
      snackbar.error("Enter a valid email first.");
      goToStep(1);
      return;
    }

    if (!validateOtp(6)) return;

    const response = await passwordVerifyOTP({
      email: email.value,
      otp: otp.value,
    });

    snackbar.success(response?.message || "OTP verified. Now set a new password.");
    goToStep(3);
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to verify OTP";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
    }

    if (!otpError.value) {
      otpError.value = message;
    }
  } finally {
    isVerifyingOtp.value = false;
  }
}

async function handleChangePassword() {
  otpError.value = "";
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  validateOtp(6);

  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  if (emailError.value || otpError.value || passwordError.value || confirmPasswordError.value) {
    return;
  }

  try {
    isResettingPassword.value = true;
    const response = await resetPassword({
      email: email.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });

    snackbar.success(response?.message || "Password changed successfully.");
    router.push({ name: "login.page" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to reset password";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length) {
        confirmPasswordError.value = errors.password_confirmation[0];
      }
    }

    if (!emailError.value && !otpError.value && !passwordError.value && !confirmPasswordError.value) {
      snackbar.error(message);
    }
  } finally {
    isResettingPassword.value = false;
  }
}

async function handleSendOtp() {
  if (!validateInputs()) return;
  try {
    isSendingOtp.value = true;
    emailError.value = "";
    otpError.value = "";
    const response = await resetPasswordOTP({ email: email.value });
    snackbar.success(response.message || "OTP sent to your email.");
    otp.value = "";
    otpError.value = "";
    goToStep(2);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to send OTP";
    emailError.value = message;
  } finally {
    isSendingOtp.value = false;
  }
}

async function handleResendOtp() {
  if (step.value !== 2) return;
  await handleSendOtp();
  step.value = 2;
}

return (_ctx: any,_cache: any) => {
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppAuthBrand, {
        data: brandData.value,
        logoAlt: companyName.value,
        "subtitle-size": "16px",
        "title-color": "#151a24",
        "subtitle-color": "#64748b",
        gap: "10px"
      }, null, 8, ["data", "logoAlt"]),
      (step.value === 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_v_form, { class: "form-style" }, {
              default: _withCtx(() => [
                _createVNode(AppTextField, {
                  modelValue: email.value,
                  "onUpdate:modelValue": [
                    _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
                    _cache[1] || (_cache[1] = ($event: any) => (emailError.value = ''))
                  ],
                  type: "email",
                  autocomplete: "off",
                  "prepend-inner-icon": "mdi-email-outline",
                  placeholder: "Email address",
                  class: "auth-input",
                  "error-messages": emailError.value
                }, null, 8, ["modelValue", "error-messages"])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(AppButtonFlat, {
                block: "",
                onClick: handleSendOtp,
                loading: isSendingOtp.value,
                disabled: isSendingOtp.value
              }, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode(" Send OTP ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"])
            ])
          ]))
        : (step.value === 2)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_component_v_form, { class: "form-style" }, {
                default: _withCtx(() => [
                  _createVNode(AppOTPField, {
                    modelValue: otp.value,
                    "onUpdate:modelValue": [
                      _cache[2] || (_cache[2] = ($event: any) => ((otp).value = $event)),
                      _cache[3] || (_cache[3] = ($event: any) => (otpError.value = ''))
                    ],
                    length: 6,
                    type: "number",
                    "error-message": otpError.value
                  }, null, 8, ["modelValue", "error-message"])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(AppButtonFlat, {
                  block: "",
                  onClick: handleVerifyOtp,
                  loading: isVerifyingOtp.value,
                  disabled: isVerifyingOtp.value
                }, {
                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                    _createTextVNode(" Verify OTP ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(AppButtonFlat, {
                  color: "secondary",
                  variant: "outlined",
                  class: "reset-actions__btn",
                  loading: isSendingOtp.value,
                  disabled: isSendingOtp.value,
                  onClick: handleResendOtp
                }, {
                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                    _createTextVNode(" Resend OTP ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_component_v_form, { class: "form-style" }, {
                default: _withCtx(() => [
                  _createVNode(AppTextField, {
                    modelValue: password.value,
                    "onUpdate:modelValue": [
                      _cache[4] || (_cache[4] = ($event: any) => ((password).value = $event)),
                      _cache[5] || (_cache[5] = ($event: any) => (passwordError.value = ''))
                    ],
                    type: showPassword.value ? 'text' : 'password',
                    autocomplete: "new-password",
                    "prepend-inner-icon": "mdi-lock-outline",
                    "append-inner-icon": showPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              ,
                    placeholder: "New password",
                    class: "auth-input",
                    "error-messages": passwordError.value,
                    "onClick:appendInner": _cache[6] || (_cache[6] = ($event: any) => (showPassword.value = !showPassword.value))
                  }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"]),
                  _createVNode(AppTextField, {
                    modelValue: confirmPassword.value,
                    "onUpdate:modelValue": [
                      _cache[7] || (_cache[7] = ($event: any) => ((confirmPassword).value = $event)),
                      _cache[8] || (_cache[8] = ($event: any) => (confirmPasswordError.value = ''))
                    ],
                    type: showConfirmPassword.value ? 'text' : 'password',
                    autocomplete: "new-password",
                    "prepend-inner-icon": "mdi-lock-check-outline",
                    "append-inner-icon": showConfirmPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              ,
                    placeholder: "Confirm new password",
                    class: "auth-input",
                    "error-messages": confirmPasswordError.value,
                    "onClick:appendInner": _cache[9] || (_cache[9] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value))
                  }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(AppButtonFlat, {
                  block: "",
                  onClick: handleChangePassword,
                  loading: isResettingPassword.value,
                  disabled: isResettingPassword.value
                }, {
                  default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                    _createTextVNode(" Change Password ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ])
            ]))
    ])
  ]))
}
}

})